<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { X, Maximize2, Minimize2 } from 'lucide-vue-next'
import { useWindowSize } from '@vueuse/core'

const windowSize = useWindowSize()

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  isFullWidth: {
    type: Boolean,
    default: false
  },
  layout: {
    type: String,
    default: '' // '', 'float-big', 'full'
  },
  show: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['toggle-full-width', 'close'])

const isBig = computed(() => props.layout === 'float-big')
const isFull = computed(() => props.layout === 'full')
const isLargeViewport = computed(() => windowSize.width.value > 1024)
const isShow = ref(props.show)
const drawerHeight = ref(25)
const isDragging = ref(false)
const headerHeight = ref(0)
let startY = 0

// Sync isShow with props.show
watch(() => props.show, (newVal) => {
  isShow.value = newVal
})

// Get the header height dynamically
onMounted(() => {
  const headerEl = document.querySelector('.sidebar-panel-header')
  if (headerEl) {
    headerHeight.value = headerEl.offsetHeight
  }
})

const startDrag = (e) => {
  isDragging.value = true
  startY = e.touches ? e.touches[0].clientY : e.clientY
}

const onDrag = (e) => {
  if (!isDragging.value) return
  const currentY = e.touches ? e.touches[0].clientY : e.clientY
  const diff = startY - currentY
  const newHeight = drawerHeight.value + (diff / window.innerHeight) * 100

  const minHeight = Math.max(15, (headerHeight.value / window.innerHeight) * 100 + 5)
  const maxHeight = 85

  if (newHeight <= maxHeight && newHeight >= minHeight) {
    drawerHeight.value = newHeight
  }
  startY = currentY
}

const endDrag = () => {
  const minHeight = Math.max(20, (headerHeight.value / window.innerHeight) * 100 + 10)
  const maxHeight = 85

  if (drawerHeight.value < minHeight) {
    drawerHeight.value = minHeight
  } else if (drawerHeight.value > maxHeight) {
    drawerHeight.value = maxHeight
  }

  isDragging.value = false
}

const handleClose = () => {
  isShow.value = false
  emit('close')
}
</script>

<template>
  <!-- Desktop sidebar -->
  <div
    v-if="isShow && isLargeViewport"
    class="text-sm relative z-10 bg-background flex flex-col rounded-md pointer-events-auto transition-all duration-300 h-full"
    :class="{
      'w-full': isFull || isFullWidth,
      'min-w-[720px] max-w-[720px]': isBig && !isFull && !isFullWidth,
      'min-w-[320px] max-w-[320px]': !isBig && !isFull && !isFullWidth
    }"
  >
    <div class="p-1 flex items-center justify-between bg-background shadow-sm rounded-t-md flex-shrink-0">
      <div class="font-bold">
        {{ title }}
      </div>
      <div class="flex items-center gap-1">
        <Button
          @click="emit('toggle-full-width')"
          variant="ghost"
          class="w-6 h-6 p-0 rounded-full"
          :title="isFullWidth ? 'Exit full width' : 'Expand to full width'"
        >
          <Minimize2 v-if="isFullWidth" class="w-4 h-4" />
          <Maximize2 v-else class="w-4 h-4" />
        </Button>
        <Button
          @click="handleClose"
          variant="ghost"
          class="w-6 h-6 p-0 rounded-full"
          title="Close"
        >
          <X class="w-5 h-5" />
        </Button>
      </div>
    </div>
    <div class="flex-1 overflow-y-auto">
      <slot />
    </div>
  </div>

  <!-- Mobile drawer -->
  <div
    v-if="isShow && !isLargeViewport"
    class="fixed bottom-0 left-0 right-0 bg-background shadow-lg z-50 overflow-hidden pointer-events-auto rounded-t-xl flex flex-col"
    :style="{
      height: `${Math.min(drawerHeight, 85)}vh`,
      maxHeight: '85vh'
    }"
    @mousemove="onDrag"
    @mouseup="endDrag"
    @touchmove="onDrag"
    @touchend="endDrag"
  >
    <div class="sidebar-panel-header p-1 shadow-sm z-10 cursor-move flex-shrink-0"
      @mousedown="startDrag"
      @touchstart="startDrag"
    >
      <div class="flex justify-center">
        <div class="w-28 h-1.5 bg-muted rounded mt-1"></div>
      </div>
      <div class="flex items-center justify-between">
        <div class="font-bold pl-1">{{ title }}</div>
        <div class="flex items-center gap-1">
          <Button
            @click.stop="emit('toggle-full-width')"
            variant="ghost"
            class="w-6 h-6 p-0 rounded-full"
            :title="isFullWidth ? 'Exit full width' : 'Expand to full width'"
          >
            <Minimize2 v-if="isFullWidth" class="w-4 h-4 text-muted-foreground" />
            <Maximize2 v-else class="w-4 h-4 text-muted-foreground" />
          </Button>
          <Button
            @click="handleClose"
            variant="ghost"
            class="w-6 h-6 p-0 rounded-full"
            title="Close"
          >
            <X class="w-5 h-5 text-muted-foreground" />
          </Button>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <slot />
    </div>
  </div>
</template>

<style scoped>
body {
  user-select: none;
}
</style>
